import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm';
const DATE_FORMAT = 'dd/MM/yyyy';
const TIME_FORMAT = 'HH:mm:ss';

/**
 * Format an ISO date string to Brazilian format: dd/MM/yyyy HH:mm
 */
export function formatDateTime(isoString: string): string {
  try {
    return format(parseISO(isoString), DATE_TIME_FORMAT, { locale: ptBR });
  } catch {
    return isoString;
  }
}

/**
 * Format an ISO date string to date only: dd/MM/yyyy
 */
export function formatDate(isoString: string): string {
  try {
    return format(parseISO(isoString), DATE_FORMAT, { locale: ptBR });
  } catch {
    return isoString;
  }
}

/**
 * Format an ISO date string to time only: HH:mm:ss
 */
export function formatTime(isoString: string): string {
  try {
    return format(parseISO(isoString), TIME_FORMAT, { locale: ptBR });
  } catch {
    return isoString;
  }
}

/**
 * Format relative time (e.g., "há 5 minutos")
 */
export function formatRelative(isoString: string): string {
  try {
    return formatDistanceToNow(parseISO(isoString), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return isoString;
  }
}

/**
 * Returns true if the date is more than `minutes` ago.
 */
export function isOlderThan(isoString: string, minutes: number): boolean {
  try {
    const date = parseISO(isoString);
    const diffMs = Date.now() - date.getTime();
    return diffMs > minutes * 60 * 1000;
  } catch {
    return true;
  }
}

/**
 * Returns an ISO string for `hoursAgo` hours before now.
 */
export function hoursAgoIso(hours: number): string {
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

/**
 * Format duration in seconds to human-readable string.
 */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}
